import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

export type ParamType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'stringArray'
  | 'json';

export interface SchemaEntry<T = any> {
  type: ParamType;
  default: T;
}

export type Schema<T extends Record<string, any>> = {
  [K in keyof T]: SchemaEntry<T[K]>;
};

const serialize = (type: ParamType, value: unknown): string | null => {
  switch (type) {
    case 'boolean':
      return value ? 'true' : null;
    case 'stringArray': {
      const arr = (value as string[]) ?? [];
      return arr.length ? arr.join(',') : null;
    }
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : null;
    case 'json': {
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isNullish = value === undefined || value === null;
      if (isNullish || isEmptyArray) return null;
      return JSON.stringify(value);
    }
    default:
      return value === undefined || value === null || value === ''
        ? null
        : String(value);
  }
};
const deserialize = (type: ParamType, raw: string | null) => {
  if (raw === null) return undefined;
  switch (type) {
    case 'boolean':
      return raw === 'true';
    case 'number': {
      const n = Number(raw);
      return Number.isNaN(n) ? undefined : n;
    }
    case 'stringArray':
      return raw
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
    case 'json':
      try {
        return JSON.parse(raw);
      } catch {
        return undefined; // falls back to the schema default
      }
    default:
      return raw;
  }
};

export function useUrlFilters<T extends Record<string, any>>(
  schema: Schema<T>,
) {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = useMemo(() => {
    const result = {} as T;
    (Object.keys(schema) as (keyof T)[]).forEach(key => {
      const entry = schema[key];
      const parsed = deserialize(entry.type, searchParams.get(key as string));
      result[key] = (
        parsed === undefined ? entry.default : parsed
      ) as T[typeof key];
    });
    return result;
    // searchParams.toString() keeps the memo stable across renders where
    // the params object identity changes but content doesn't.
  }, [searchParams.toString(), schema]);

  const setFilters = useCallback(
    (partial: Partial<T>, options?: {replace?: boolean}) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          (Object.keys(partial) as (keyof T)[]).forEach(key => {
            const serialized = serialize(schema[key].type, partial[key]);
            if (serialized === null) {
              next.delete(key as string);
            } else {
              next.set(key as string, serialized);
            }
          });
          return next;
        },
        {replace: options?.replace ?? true},
      );
    },
    [schema, setSearchParams],
  );

  const setFilter = useCallback(
    <K extends keyof T>(key: K, value: T[K], options?: {replace?: boolean}) =>
      setFilters({[key]: value} as unknown as Partial<T>, options),
    [setFilters],
  );

  const resetFilter = useCallback(
    (key: keyof T) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete(key as string);
          return next;
        },
        {replace: true},
      );
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        Object.keys(schema).forEach(key => next.delete(key));
        return next;
      },
      {replace: true},
    );
  }, [schema, setSearchParams]);

  return {values, setFilter, setFilters, resetFilter, reset};
}
